import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChangeEvent, useState } from 'react';
import RankOrderViewModel from '../ViewModels/RankOrderViewModel';
import RankOrderExportViewModel from '../ViewModels/RankOrderExportViewModel';

interface IProps {
    dialogOpen: boolean;
    handleClose: () => void;
    rankOrder: RankOrderViewModel;
    exportRankOrder: (filters: RankOrderExportViewModel) => void;
}

const sectionSx = { border: '1px solid #ccc', borderRadius: 1, p: 1, mb: 1 };

export default function ExportDialog(props: IProps) {
    const [filters, setFilters] = useState(new RankOrderExportViewModel());

    const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;

        setFilters(prev => {
            const updated = { ...prev, [name]: checked };

            if (name === 'selectionParameters' && !checked) {
                updated.regionsAndAreas = false;
                updated.mainParameters = false;
                updated.secondaryFiltering = false;
                updated.includeEnhancedSiteList = false;
            }

            return updated;
        });
    };


    const handleRadio = (name: keyof RankOrderExportViewModel) => (e: ChangeEvent<HTMLInputElement>) =>
        setFilters(f => ({
            ...f,
            [name]: (e.target as HTMLInputElement).value
        }));

    const reset = () => {
        setFilters(new RankOrderExportViewModel());
        props.handleClose();
    };

    const handleText = (e: ChangeEvent<HTMLInputElement>) => {
        setFilters(f => ({
            ...f,
            userComments: (e.target as HTMLInputElement).value
        }))
    }

    const submit = () => {
        props.exportRankOrder(filters);
        reset();
    };

    return (
        <Dialog
            open={props.dialogOpen}
            onClose={props.handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Rank Order Export</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <Box sx={sectionSx}>
                            <Typography variant="subtitle2">Report Options</Typography>
                            <TextField
                                label="Report Name"
                                value={filters.reportTitle}
                                onChange={e =>
                                    setFilters(f => ({
                                        ...f,
                                        reportTitle: e.target.value
                                    }))
                                }
                                fullWidth
                                size="small"
                                margin="dense"
                            />
                            <RadioGroup
                                row
                                name="exportType"
                                value={filters.exportType}
                                onChange={handleRadio('exportType')}
                            >
                                <FormControlLabel
                                    value="pdf"
                                    control={<Radio size="small" />}
                                    label="PDF"
                                />
                                <FormControlLabel
                                    value="csv"
                                    control={<Radio size="small" />}
                                    label="CSV"
                                />
                            </RadioGroup>
                        </Box>
                    </Grid>
                    {filters.exportType === 'pdf' && (
                        <>
                            <Grid size={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="subtitle2">
                                        Selection Parameters
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="selectionParameters"
                                                    checked={filters.selectionParameters}
                                                    onChange={handleCheck}
                                                    size="small"
                                                />
                                            }
                                            label="Details of Selection Parameters"
                                            sx={{ mb: 0.5 }}
                                        />
                                        <Box sx={{ pl: 2 }}>
                                            {[
                                                'regionsAndAreas',
                                                'mainParameters',
                                                'secondaryFiltering',
                                                'includeEnhancedSiteList'
                                            ].map(name => (
                                                <FormControlLabel
                                                    key={name}
                                                    control={
                                                        <Checkbox
                                                            name={name}
                                                            checked={filters[name]}
                                                            onChange={handleCheck}
                                                            disabled={
                                                                !filters.selectionParameters
                                                            }
                                                            size="small"
                                                        />
                                                    }
                                                    label={{
                                                        regionsAndAreas: 'Regions & Areas',
                                                        mainParameters: 'Main Parameter Ranges',
                                                        secondaryFiltering: 'Secondary Filtering',
                                                        includeEnhancedSiteList: 'Include Enhanced Site List'
                                                    }[name]}
                                                    sx={{ mb: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    </FormGroup>
                                </Box>
                            </Grid>
                            <Grid size={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="subtitle2">
                                        Rank Order Graph
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="rankOrderGraph"
                                                    checked={filters.rankOrderGraph}
                                                    onChange={handleCheck}
                                                    size="small"
                                                />
                                            }
                                            label="Include Graph"
                                            sx={{ mb: 0.5 }}
                                        />
                                    </FormGroup>
                                </Box>
                            </Grid>
                            <Grid size={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="subtitle2">
                                        Comments
                                    </Typography>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="addUserComments"
                                                    checked={filters.addUserComments}
                                                    onChange={handleCheck}
                                                    size="small"
                                                />
                                            }
                                            label="Add User Comments to Report"
                                            sx={{ mb: 0.5 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="explanatoryCommentsIncluded"
                                                    checked={filters.explanatoryCommentsIncluded}
                                                    onChange={handleCheck}
                                                    size="small"
                                                />
                                            }
                                            label=" Include explanatory commentary in the report"
                                            sx={{ mb: 0.5 }}
                                        />
                                    </FormGroup>
                                    {filters.addUserComments &&
                                        <TextField label={"User Comments"} fullWidth onChange={handleText} />

                                    }
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={submit} size="small">
                    OK
                </Button>
                <Button onClick={reset} size="small">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}